import { Field, Form, Formik } from "formik";
import React, { useContext, useEffect, useState } from "react";
import * as Yup from "yup";

// MUI Components
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  alpha
} from "@mui/material";
import { MuiColorInput } from "mui-color-input";
import { makeStyles } from 'tss-react/mui';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SettingsIcon from '@mui/icons-material/Settings';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';

// Context and Services
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import ToastError from "../../toast/error/toastError";
import ToastSuccess from "../../toast/success/toastSuccess";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles()((theme) => {
  const isDark = theme.palette.mode === 'dark';

  return {
    dialogRoot: {
      '& .MuiDialog-paper': {
        borderRadius: 12,
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
        maxWidth: 600,
        width: '100%',
        margin: 24,
      },
      '& .MuiBackdrop-root': {
        backgroundColor: alpha(theme.palette.background.default, 0.8)
      }
    },
    dialogTitle: {
      background: isDark
        ? alpha(theme.palette.primary.dark, 0.9)
        : theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      padding: theme.spacing(2, 3),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '& h6': {
        fontSize: '1.2rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
      }
    },
    dialogContent: {
      padding: theme.spacing(3),
      backgroundColor: isDark
        ? theme.palette.background.default
        : alpha(theme.palette.background.paper, 0.8),
      overflowY: 'auto',
      maxHeight: 'calc(100vh - 290px)',
    },
    dialogActions: {
      padding: theme.spacing(2, 3),
      borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.3)
        : alpha(theme.palette.background.default, 0.5),
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(1.5),
    },
    formSection: {
      marginBottom: theme.spacing(3),
      padding: theme.spacing(2.5),
      borderRadius: 12,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.2)
        : theme.palette.background.paper,
      boxShadow: isDark
        ? `0 4px 12px ${alpha('#000', 0.1)}`
        : `0 4px 12px ${alpha('#000', 0.05)}`,
      border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      transition: 'all 0.15s ease-in-out',
      '&:hover': {
        boxShadow: isDark
          ? `0 6px 16px ${alpha('#000', 0.14)}`
          : `0 6px 16px ${alpha('#000', 0.08)}`,
      }
    },
    sectionTitle: {
      marginBottom: theme.spacing(2.5),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.2),
      fontWeight: 600,
      color: theme.palette.text.primary,
      fontSize: '1.1rem',
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
      paddingBottom: theme.spacing(1.2),
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        bottom: -1,
        width: 40,
        height: 2,
        backgroundColor: theme.palette.primary.main,
        borderRadius: 2
      }
    },
    icon: {
      fontSize: 20,
      color: theme.palette.primary.main,
    },
    textField: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 8,
        backgroundColor: isDark
          ? alpha(theme.palette.background.paper, 0.2)
          : theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.3),
          backgroundColor: isDark
            ? alpha(theme.palette.background.paper, 0.3)
            : theme.palette.background.paper,
        },
        '&.Mui-focused': {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
        }
      },
      '& .MuiOutlinedInput-input': {
        padding: theme.spacing(1.5, 1.5),
        fontSize: '0.9rem',
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.9rem',
        fontWeight: 500,
      },
      '& .MuiFormHelperText-root': {
        marginLeft: 4,
        marginTop: 4,
        fontSize: '0.75rem',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        '&.Mui-error': {
          backgroundColor: alpha(theme.palette.error.main, 0.08),
          color: theme.palette.error.main,
          padding: theme.spacing(0.4, 0.8),
          borderRadius: 4,
        }
      }
    },
    btnWrapper: {
      position: "relative",
    },
    buttonProgress: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
    saveButton: {
      borderRadius: 8,
      padding: theme.spacing(1, 3),
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.9rem',
      boxShadow: theme.shadows[2],
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        boxShadow: theme.shadows[4],
      }
    },
    cancelButton: {
      borderRadius: 8,
      padding: theme.spacing(1, 2.5),
      fontWeight: 500,
      textTransform: 'none',
      fontSize: '0.9rem',
      color: theme.palette.text.secondary,
      '&:hover': {
        backgroundColor: alpha(theme.palette.divider, 0.1),
      }
    },
    fieldLabel: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(1),
      fontWeight: 600,
      color: theme.palette.text.primary,
      fontSize: '0.9rem',
      '& svg': {
        marginRight: theme.spacing(0.8),
        color: theme.palette.primary.main,
        fontSize: '1.1rem',
        opacity: 0.9,
      }
    },
    closeButton: {
      color: theme.palette.primary.contrastText,
      backgroundColor: alpha(theme.palette.common.white, 0.1),
      borderRadius: 8,
      padding: 6,
      '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.2),
      }
    },
    titleWithIcon: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    hintText: {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.8),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.6),
      padding: theme.spacing(0.8, 1.2),
      borderRadius: 8,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.15)
        : alpha(theme.palette.background.default, 0.7),
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      '& svg': {
        fontSize: '1rem',
        color: theme.palette.primary.main,
        opacity: 0.8,
      }
    },
  };
});

const CauseSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(100, "Nome deve ter no máximo 100 caracteres")
    .required("Nome da causa é obrigatório"),
  color: Yup.string()
    .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, "Cor deve ser um código hexadecimal válido")
    .required("Cor é obrigatória"),
  description: Yup.string()
    .max(2000, "Descrição deve ter no máximo 2000 caracteres")
    .nullable(),
});

const CreateOrEditCauseModal = ({ open, onClose, causeId }) => {
  const { user: loggedInUser } = useContext(AuthContext);
  const { classes } = useStyles();

  const initialState = {
    companyId: loggedInUser.companyId,
    name: "",
    color: "#1976d2",
    description: "",
  };

  const [cause, setCause] = useState(initialState);

  useEffect(() => {
    const fetchCause = async () => {
      if (!causeId) return;
      try {
        const { data } = await api.get(`/causes/${causeId}`);
        setCause(prevState => ({
          ...prevState,
          ...data,
          // null no DB precisa virar "" pro TextField não reclamar de controlled→uncontrolled.
          description: data?.description ?? "",
        }));
      } catch (err) {
        ToastError(err);
      }
    };

    fetchCause();
  }, [causeId, open]);

  const handleClose = () => {
    onClose();
    setCause(initialState);
  };

  const handleSaveCause = async (values) => {
    try {
      if (causeId) {
        await api.put(`/causes/${causeId}`, values);
        ToastSuccess("Causa editada com sucesso!");
      } else {
        await api.post("/causes", values);
        ToastSuccess("Causa criada com sucesso!");
      }
      handleClose();
    } catch (err) {
      ToastError(err);
    }
  };

  const handleChangeValues = (e, callback) => {
    const { name, value } = e.target;
    setCause((prevState) => ({ ...prevState, [name]: value }));
    callback(e);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialogRoot}
      fullWidth
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.titleWithIcon}>
          <SettingsIcon fontSize="small" />
          <Typography variant="h6">
            {causeId ? "Editar Causa" : "Adicionar Nova Causa"}
          </Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
          className={classes.closeButton}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Formik
        initialValues={cause}
        enableReinitialize={true}
        validationSchema={CauseSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveCause(values);
            actions.setSubmitting(false);
          }, 400);
        }}
      >
        {({ values, handleChange, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent className={classes.dialogContent}>
              <Field style={{ display: "none" }} as={TextField} type="hidden" name="companyId" value={loggedInUser.companyId} />
              
              <Paper elevation={0} className={classes.formSection}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  <HelpOutlineIcon className={classes.icon} />
                  Configurações da Causa
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography className={classes.fieldLabel}>
                      <HelpOutlineIcon fontSize="small" /> Nome da Causa
                    </Typography>
                    <Field
                      as={TextField}
                      placeholder="Digite o nome da causa de atendimento"
                      name="name"
                      fullWidth
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      size="small"
                      className={classes.textField}
                      value={cause.name}
                      onChange={e => handleChangeValues(e, handleChange)}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography className={classes.fieldLabel}>
                      <LocalOfferIcon fontSize="small" /> Cor da Causa
                    </Typography>
                    <MuiColorInput
                      format="hex"
                      name="color"
                      size="small"
                      value={cause.color}
                      onChange={color => {
                        setCause((prev) => ({ ...prev, color }));
                        setFieldValue("color", color);
                      }}
                      variant="outlined"
                      error={touched.color && Boolean(errors.color)}
                      helperText={touched.color && errors.color}
                      fullWidth
                      className={classes.textField}
                    />
                    <Typography className={classes.hintText}>
                      <TipsAndUpdatesIcon fontSize="small" />
                      A cor será utilizada para identificação visual da causa nos tickets.
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className={classes.fieldLabel}>
                      <DescriptionIcon fontSize="small" /> Descrição (Opcional)
                    </Typography>
                    <Field
                      as={TextField}
                      placeholder="Descreva o que essa causa representa, quando ela deve ser selecionada, etc."
                      name="description"
                      multiline
                      minRows={3}
                      maxRows={8}
                      fullWidth
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      variant="outlined"
                      size="small"
                      className={classes.textField}
                      value={cause.description}
                      onChange={e => handleChangeValues(e, handleChange)}
                      inputProps={{ maxLength: 2000 }}
                    />
                    <Typography className={classes.hintText}>
                      <TipsAndUpdatesIcon fontSize="small" />
                      Texto interno de referência — ajuda os atendentes a escolher a causa correta. Máximo 2000 caracteres.
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </DialogContent>
            
            <DialogActions className={classes.dialogActions}>
              <Button
                onClick={handleClose}
                color="inherit"
                disabled={isSubmitting}
                variant="outlined"
                className={classes.cancelButton}
                startIcon={<CloseIcon fontSize="small" />}
              >
                Cancelar
              </Button>
              
              <div className={classes.btnWrapper}>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.saveButton}
                  startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
                >
                  {causeId ? "Salvar Alterações" : "Adicionar Causa"}
                </Button>
                {isSubmitting && (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />
                )}
              </div>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateOrEditCauseModal;